import { useCallback } from 'react';
import { saveRating } from './api';
import {
  useJudgementAutosave,
  type JudgementPayload,
  type SaveState,
} from '../rlhfShared/useJudgementAutosave';

/**
 * Autosave for a 1-5 rating in progress.
 *
 * A thin wrapper over the shared judgement autosave — the comparison track has the identical
 * problem (a choice plus reasoning, drafted while typing, flushed before navigating) and both
 * pages must behave the same way, so the logic lives in one place. See
 * `rlhfShared/useJudgementAutosave` for why baseline comparison matters here.
 */

export type { SaveState };

interface UseRatingAutosaveArgs {
  assignmentId: number | null;
  rating: number | null;
  remarks: string;
  /** Suppresses drafting while the form is being populated from the server. */
  enabled: boolean;
  /** Mirrors every committed save back into the page's queue — see the shared hook. */
  onSaved?: (committed: {
    itemId: number;
    choice: number | null;
    remarks: string;
    draft: boolean;
  }) => void;
}

export function useRatingAutosave({
  assignmentId,
  rating,
  remarks,
  enabled,
  onSaved,
}: UseRatingAutosaveArgs) {
  const save = useCallback(
    (id: number, payload: JudgementPayload<number | null>) =>
      saveRating(id, { rating: payload.choice, remarks: payload.remarks, draft: payload.draft }),
    [],
  );

  return useJudgementAutosave<number | null>({
    itemId: assignmentId,
    choice: rating,
    remarks,
    enabled,
    save,
    failureMessage: 'Could not save your rating',
    onSaved,
  });
}
